
import { Lead } from "@/types/lead";
import { toast } from "@/hooks/use-toast";
import { getStatusNotes } from "./statusNotesService";
import { supabase } from "@/integrations/supabase/client";
import { formatDistance } from "date-fns";

const basicFields = [
  'name', 
  'address', 
  'phone', 
  'email', 
  'website', 
  'category',
  'status',
  'statusNotes',
  'allNotes'
];

const additionalFields = [
  'rating',
  'reviewCount'
];

// Fetch all notes for a lead and format them as a single string
const getAllNotes = async (leadId: string): Promise<string> => {
  try {
    const { data, error } = await supabase
      .from('notes')
      .select('content, created_at, created_by, tags')
      .eq('lead_id', leadId)
      .order('created_at', { ascending: true });
    
    if (error) {
      console.error(`Error fetching notes for lead ${leadId}:`, error);
      return 'Error fetching notes';
    }
    
    if (!data || data.length === 0) {
      return '';
    }
    
    // Format the notes as a single string with dates and separators
    return data
      .map(note => {
        const date = new Date(note.created_at);
        const formattedDate = `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
        const tags = note.tags && note.tags.length > 0 ? `[${note.tags.join(', ')}]` : '';
        
        return `${formattedDate}: ${note.content} ${tags}`;
      })
      .join('\n\n');
  } catch (error) {
    console.error(`Error processing notes for lead ${leadId}:`, error);
    return 'Error processing notes';
  }
};

export const exportLeadsToCSV = async (leads: Lead[], includeAllFields = false): Promise<string> => {
  try {
    // Combine fields based on selection
    const fields = [...basicFields, ...(includeAllFields ? additionalFields : [])];
    
    // Create header row
    const header = fields.join(',');
    
    // Create data rows with improved error handling
    const rows = await Promise.all(leads.map(async (lead) => {
      let statusNotes = '';
      let allNotes = '';
      
      try {
        statusNotes = await getStatusNotes(lead.id);
        allNotes = await getAllNotes(lead.id);
      } catch (error) {
        console.error(`Error fetching notes for lead ${lead.id}:`, error);
        statusNotes = 'Error fetching notes';
        allNotes = 'Error fetching notes';
      }
      
      return fields.map(field => {
        let value = '';
        
        switch(field) {
          case 'email':
            if (typeof lead.email === 'string') {
              value = lead.email;
            } else if (Array.isArray(lead.email)) {
              value = lead.email[0]?.email || '';
            } else if (lead.email && typeof lead.email === 'object') {
              value = lead.email.email || '';
            }
            break;
          case 'statusNotes':
            value = statusNotes;
            break;
          case 'allNotes':
            value = allNotes;
            break;
          default:
            value = (lead[field as keyof Lead] as unknown as string | number)?.toString() || '';
        }
        
        if (value && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
          value = `"${value.replace(/"/g, '""')}"`;
        }
        
        return value;
      }).join(',');
    }));
    
    return [header, ...rows].join('\n');
  } catch (error) {
    console.error('Error exporting leads to CSV:', error);
    toast({
      title: "Export Failed",
      description: "An error occurred while exporting leads. Please try again.",
      variant: "destructive",
    });
    return '';
  }
};
